






































































































































import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { mdbRow, mdbCol, mdbSelect, mdbInput } from '@folky-tools/mdbvue';
import { required, requiredIf, minValue } from 'vuelidate/lib/validators';
import { ReferenceBasicData as ReferenceBasicDataModel } from '@/models/references/reference-basic-data';
import { ReferenceType } from '@/models/references/reference-type';
import { Business } from '@/models/businesses/business';
import { Category } from '@/models/references-classification/category';
import { businessesService, referencesClassificationService } from '@/services';

@Component({
    components: {
        mdbRow,
        mdbCol,
        mdbSelect,
        mdbInput,
        ReferenceCategories: () => import('@/components/references/ReferenceCategories.vue'),
        Wysiwyg: () => import('@/components/shared/Wysiwyg.vue')
    },
    validations: {
        reference: {
            businessId: { required, minValue: minValue(1) },
            title: {
                required: requiredIf(model => {
                    return model.type === 1 || model.type === 3;
                })
            }
        }
    }
})
export default class ReferenceBasicData extends Vue {
    @Prop({ type: Boolean, default: false }) private readOnly?: boolean;
    @Prop(ReferenceBasicDataModel) private reference!: ReferenceBasicDataModel;
    private businesses: Business[] = [];
    private initialSelectedCategories: Category[] = [];
    private topLevelCategories: Category[] = [];
    private principalSkuPrice: string = '';
    private depositSkuPrice: string = '';
    private totalValueSkuPrice: string = '';
    private firstLoad = {
        principalSkuPrice: true,
        depositSkuPrice: true,
        totalValueSkuPrice: true
    };

    @Watch('principalSkuPrice')
    private onPrincipalSkuPriceChanged(newValue: string, oldValue: string): void {
        if (!this.firstLoad.principalSkuPrice) {
            this.reference.principalSkuPrice = (this.$refs.principalSkuPrice as any).$refs.input.$ci.numberValue;
        }
        this.firstLoad.principalSkuPrice = false;
    }
    @Watch('depositSkuPrice')
    private ondepositSkuPriceChanged(newValue: string, oldValue: string): void {
        if (!this.firstLoad.depositSkuPrice) {
            this.reference.depositSkuPrice = (this.$refs.depositSkuPrice as any).$refs.input.$ci.numberValue;
        }
        this.firstLoad.depositSkuPrice = false;
    }
    @Watch('totalValueSkuPrice')
    private onTotalValueSkuPriceChanged(newValue: string, oldValue: string): void {
        if (!this.firstLoad.totalValueSkuPrice) {
            this.reference.totalValueSkuPrice = (this.$refs.totalValueSkuPrice as any).$refs.input.$ci.numberValue;
        }
        this.firstLoad.totalValueSkuPrice = false;
    }

    private async mounted(): Promise<void> {
        this.businesses = await businessesService.getBusinesses();
        if (this.reference.id > 0) {
            this.onChangeBusiness(this.reference.businessId, '');
            if (this.reference.principalSkuPrice) {
                this.principalSkuPrice = this.reference.principalSkuPrice.toString();
            }
            if (this.reference.depositSkuPrice) {
                this.depositSkuPrice = this.reference.depositSkuPrice.toString();
            }
            if (this.reference.totalValueSkuPrice) {
                this.totalValueSkuPrice = this.reference.totalValueSkuPrice.toString();
            }
        } else {
            if (this.businesses.length === 1) {
                this.businesses[0].selected = true;
            }
        }
    }

    private async onChangeBusiness(value: number, text: string): Promise<void> {
        if (value) {
            this.reference.businessId = value;
            this.reference.projectId = this.businesses.find(b => b.id === value)!.mainProjectId;
            this.topLevelCategories = await this.getTopLevelCategories();
        }
    }

    private async setCategoryForReference(selectedCategory: Category, loadingData: boolean): Promise<void> {
        if (selectedCategory && selectedCategory.id! > 0) {
            if (!loadingData) {
                this.reference.categoryId = selectedCategory.id;
                this.setTopLevelCategoryForReference();
            }
        } else {
            this.reference.categoryId = undefined;
        }
    }

    private async setTopLevelCategoryForReference(): Promise<void> {
        if (this.reference.type === ReferenceType.Product) {
            const selectedTopLevelCategory = this.topLevelCategories.find(c => c.selected);
            if (selectedTopLevelCategory && selectedTopLevelCategory.id! > 0) {
                this.reference.topLevelCategoryId = selectedTopLevelCategory.id;
            }
        }
    }

    private async getTopLevelCategories(): Promise<Category[]> {
        let topLevelCategories = await referencesClassificationService.getTopLevelCategories(this.reference.businessId);
        topLevelCategories.forEach(c => (c.level = 1));

        topLevelCategories = this.sortCategoriesAlphabetically(topLevelCategories);
        topLevelCategories = this.addEllipsisOptionToCategories(topLevelCategories);
        topLevelCategories = this.addDisableOptionToCategories(topLevelCategories);

        if (this.reference.categoryId && this.reference.categoryId > 0) {
            this.initialSelectedCategories = await referencesClassificationService.getCategoriesTreeByCategoryId(this.reference.businessId, this.reference.categoryId);
            this.initialSelectedCategories.forEach(c => (c.selected = true));
            topLevelCategories.forEach(c => {
                if (this.initialSelectedCategories.some(sc => sc.id === c.id)) {
                    c.selected = true;
                }
            });
        }

        if (!topLevelCategories.some(c => c.selected)) {
            topLevelCategories = topLevelCategories.map(c => {
                if (c.id === -1) {
                    c.selected = true;
                }
                return c;
            });
        }

        return topLevelCategories;
    }

    private sortCategoriesAlphabetically(categories: Category[]): Category[] {
        const hashedCategories = this.getHashedCategories(categories);
        return this.getCategoriesSortedAlphabetically(categories, hashedCategories, null, []);
    }

    private getHashedCategories(categories: Category[]): any {
        const categoriesHash: any = {};
        for (const category of categories) {
            if (categoriesHash[category.parentCategoryId!] === undefined) {
                categoriesHash[category.parentCategoryId!] = [];
            }
            categoriesHash[category.parentCategoryId!].push(category);
        }

        return categoriesHash;
    }

    private getCategoriesSortedAlphabetically(allCategories: Category[], categoriesHash: any, key: number | null, result: Category[]): Category[] {
        if (categoriesHash[key!] === undefined) {
            return [];
        }

        const categories: Category[] = categoriesHash[key!].sort((left: Category, right: Category) => {
            return left.text!.localeCompare(right.text!, this.$i18n.locale, { sensitivity: 'base' });
        });

        for (const category of categories) {
            result.push(category);
            this.getCategoriesSortedAlphabetically(allCategories, categoriesHash, category.id!, result);
        }

        return result;
    }

    private addEllipsisOptionToCategories(categories: Category[]): Category[] {
        return [new Category({ id: -1, businessId: this.reference.businessId, name: this.$t('common.ellipsis').toString(), level: 1 })].concat(categories);
    }

    private addDisableOptionToCategories(categories: Category[]): Category[] {
        return [new Category({ id: 0, level: 1, disabled: true, optgroup: true })].concat(categories);
    }

    private onUpdateDescription(htmlContent: string): void {
        this.reference.description = htmlContent;
    }

    private onUpdateParticularConditions(htmlContent: string): void {
        this.reference.particularConditions = htmlContent;
    }

    private hasErrors(): boolean {
        this.$v.reference.$touch();
        if (this.$v.reference.$error) {
            return true;
        }
        return false;
    }
}
